<script lang="ts">
import { Image } from '@builder.io/sdk-svelte';
import { onMount } from 'svelte';
import { formatTallBuilderImageUrl } from '$lib/utils/formatImageUrl';
import { sanitizeRichTextHtml } from '$lib/utils/sanitise.js';
import { getLinkAttributes, prepareHtmlLinks, stripSiteDomain } from '$lib/utils/url.js';
import {
    EMediaAndTextColumnCount,
    EMediaAndTextCopyAlignment,
    EMediaAndTextLayoutType,
    EMediaAndTextMediaPositionHorizontal,
    EMediaAndTextMediaPositionVertical,
    EMediaAndTextVerticalAlignment,
    ETitleFontSizes,
} from '../../../config/enums.js';
import SectionHeader from '../../atoms/SectionHeader/SectionHeader.svelte';
import SectionText from '../../atoms/SectionText/SectionText.svelte';

const MEDIA_IMAGE_SIZES =
    '(max-width: 767px) calc(100vw - 32px), (max-width: 1199px) calc(100vw - 48px), 50vw';

// Dynamic import for VideoPlayer to reduce initial bundle size
let VideoPlayerComponent:
    | typeof import('../../organisms/VideoPlayer/VideoPlayer.svelte').default
    | null = $state(null);
let isLoaded = $state(false);

type TExtraImage = {
    src?: string;
    alt?: string;
    videoSrc?: string;
};

type TImageLike = {
    src?: string;
    [key: string]: unknown;
};

let {
    layoutType = EMediaAndTextLayoutType.MT,
    columns = EMediaAndTextColumnCount.SINGLE,
    hideBlock = false,
    fullBleed = false,
    heading = '',
    headingLevel = 'h3' as const,
    titleSize = ETitleFontSizes.H3,
    description1 = '',
    description2 = '',
    stickyText = false,
    copyAlignment = EMediaAndTextCopyAlignment.LEFT,
    verticalAlignment = EMediaAndTextVerticalAlignment.TOP,
    mainImage = {},
    mainImageLink = '',
    muxId = '',
    mobileImage = {},
    muxIdMobile = '',
    mediaPositionHorizontal = EMediaAndTextMediaPositionHorizontal.RIGHT,
    mediaPositionVertical = EMediaAndTextMediaPositionVertical.BOTTOM,
    secondaryImage = {},
    secondaryImageLink = '',
    secondaryVideoSrc = '',
    additionalMedia = [],
    backgroundColour = '',
    txtColour = '',
    showMuteToggle = false,
} = $props();

// Load VideoPlayer component when needed
onMount(async () => {
    if (
        !muxId &&
        !muxIdMobile &&
        !secondaryVideoSrc &&
        !additionalMedia.some((media) => media.videoSrc)
    ) {
        isLoaded = true;
        return;
    }

    try {
        const module = await import('../../organisms/VideoPlayer/VideoPlayer.svelte');
        if (module?.default) {
            VideoPlayerComponent = module.default;
            isLoaded = true;
        } else {
            console.error('VideoPlayer component module.default is undefined');
            VideoPlayerComponent = null;
            isLoaded = true;
        }
    } catch (error) {
        console.error('Failed to load VideoPlayer component:', error);
        VideoPlayerComponent = null;
        isLoaded = true;
    }
});

function sanitizeClassName(name: string): string {
    // Remove invalid characters and replace them with a hyphen
    return name.replace(/[^a-zA-Z0-9-_]/g, '-');
}
const safeLayoutType = $derived(sanitizeClassName(layoutType));

// Sanitise rich text content from Builder.io
const sanitisedDescription1 = $derived(prepareHtmlLinks(sanitizeRichTextHtml(description1)));
const sanitisedDescription2 = $derived(prepareHtmlLinks(sanitizeRichTextHtml(description2)));

const sanitisedMainImageLink = $derived(stripSiteDomain(mainImageLink));
const sanitisedSecondaryImageLink = $derived(stripSiteDomain(secondaryImageLink));
const mainImageLinkAttributes = $derived(getLinkAttributes(sanitisedMainImageLink));
const secondaryImageLinkAttributes = $derived(getLinkAttributes(sanitisedSecondaryImageLink));
const hasBackgroundColour = $derived(backgroundColour.trim().length > 0);
</script>

{#if !hideBlock}
    <div
        class="media-and-text-wrapper"
        class:media-and-text-wrapper--has-background-colour={hasBackgroundColour}
        class:media-and-text-wrapper--layout-is-fullbleed={fullBleed}
        style="--bg-colour: {backgroundColour}; --txt-colour: {txtColour};"
    >
        <div
            class="
            media-and-text
            media-and-text--columns-{columns}
            media-and-text--layoutType-{safeLayoutType}
            media-and-text--mediaPositionHorizontal-is-{mediaPositionHorizontal}
            media-and-text--mediaPositionVertical-is-{mediaPositionVertical}
            media-and-text--copyAlignment-is-{copyAlignment}
            media-and-text--verticalAlignment-is-{verticalAlignment}
            "
            class:media-and-text--layout-is-fullbleed={fullBleed}
            class:media-and-text--hasSecondaryMedia={secondaryImage?.src ||
                secondaryImage?.image ||
                secondaryVideoSrc}
            class:media-and-text--hasSecondaryDescription={description2}
        >
            {#if layoutType !== EMediaAndTextLayoutType.MO && (heading || description1 || (columns === EMediaAndTextColumnCount.DOUBLE && layoutType === EMediaAndTextLayoutType.TO && description2))}
                <div
                    class="media-and-text__text"
                    class:media-and-text__text--is-sticky={stickyText}
                >
                    {#if heading}
                        <div class="media-and-text__heading">
                            <SectionHeader {headingLevel} {titleSize}>{heading}</SectionHeader>
                        </div>
                    {/if}

                    {#if sanitisedDescription1 || (columns === EMediaAndTextColumnCount.DOUBLE && layoutType === EMediaAndTextLayoutType.TO && sanitisedDescription2)}
                        <div class="media-and-text__descriptions">
                            {#if sanitisedDescription1}
                                <SectionText
                                    >{@html sanitisedDescription1}</SectionText
                                >
                            {/if}
                            {#if columns === EMediaAndTextColumnCount.DOUBLE && layoutType === EMediaAndTextLayoutType.TO}
                                {#if sanitisedDescription2}
                                    <SectionText
                                        >{@html sanitisedDescription2}</SectionText
                                    >
                                {/if}
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}

            {#snippet imageSnippet(image: TImageLike)}
                <Image
                    {...image as any}
                    src={formatTallBuilderImageUrl(image.src, 1600)}
                    sizes={MEDIA_IMAGE_SIZES}
                />
            {/snippet}

            {#if layoutType !== EMediaAndTextLayoutType.TO}
                <div class="media-and-text__media">
                    {#if mainImage?.src || mainImage?.image || muxId || mobileImage?.src || mobileImage?.image || muxIdMobile}
                        <!-- Desktop media -->
                        <div
                            class="media-and-text__source media-and-text__source--primary media-and-text__source--desktop"
                            class:media-and-text__source--has-mobile={mobileImage?.src ||
                                mobileImage?.image ||
                                muxIdMobile}
                        >
                            {#if muxId && isLoaded && VideoPlayerComponent}
                                {@const VideoPlayer = VideoPlayerComponent}
                                <VideoPlayer
                                    {muxId}
                                    autoPlay={true}
                                    {showMuteToggle}
                                />
                            {:else if mainImage?.src || mainImage?.image}
                                {#if sanitisedMainImageLink}
                                    <a href={sanitisedMainImageLink} {...mainImageLinkAttributes} aria-label={mainImage?.altText || mainImage?.alt || undefined}>
                                        {@render imageSnippet(mainImage)}
                                    </a>
                                {:else}
                                    {@render imageSnippet(mainImage)}
                                {/if}
                            {/if}
                        </div>
                        <!-- Mobile media (only if mobile-specific media provided) -->
                        {#if mobileImage?.src || mobileImage?.image || muxIdMobile}
                            <div
                                class="media-and-text__source media-and-text__source--primary media-and-text__source--mobile"
                            >
                                {#if muxIdMobile && isLoaded && VideoPlayerComponent}
                                    {@const VideoPlayer = VideoPlayerComponent}
                                    <VideoPlayer
                                        muxId={muxIdMobile}
                                        autoPlay={true}
                                        {showMuteToggle}
                                    />
                                {:else if mobileImage?.src || mobileImage?.image}
                                    {#if sanitisedMainImageLink}
                                        <a href={sanitisedMainImageLink} {...mainImageLinkAttributes} aria-label={mobileImage?.altText || mobileImage?.alt || undefined}>
                                            {@render imageSnippet(mobileImage)}
                                        </a>
                                    {:else}
                                        {@render imageSnippet(mobileImage)}
                                    {/if}
                                {/if}
                            </div>
                        {/if}
                    {/if}
                    {#if columns === EMediaAndTextColumnCount.DOUBLE && layoutType === EMediaAndTextLayoutType.MO}
                        {#if secondaryImage?.src || secondaryImage?.image || secondaryVideoSrc}
                            <div
                                class="media-and-text__source media-and-text__source--secondary"
                            >
                                {#if secondaryVideoSrc && isLoaded && VideoPlayerComponent}
                                    {@const VideoPlayer = VideoPlayerComponent}
                                    <VideoPlayer
                                        muxId={secondaryVideoSrc}
                                        autoPlay={true}
                                        {showMuteToggle}
                                    />
                                {:else if secondaryImage?.src || secondaryImage?.image}
                                    {#if sanitisedSecondaryImageLink}
                                        <a href={sanitisedSecondaryImageLink} {...secondaryImageLinkAttributes} aria-label={secondaryImage?.altText || secondaryImage?.alt || undefined}>
                                            {@render imageSnippet(
                                                secondaryImage,
                                            )}
                                        </a>
                                    {:else}
                                        {@render imageSnippet(secondaryImage)}
                                    {/if}
                                {/if}
                            </div>
                        {/if}
                    {/if}
                    {#if columns === EMediaAndTextColumnCount.DOUBLE && layoutType === EMediaAndTextLayoutType.MT && additionalMedia.length > 0}
                        {#each additionalMedia as image}
                            <div
                                class="media-and-text__source media-and-text__source--extra"
                            >
                                {#if image.videoSrc && isLoaded && VideoPlayerComponent}
                                    {@const VideoPlayer = VideoPlayerComponent}
                                    <VideoPlayer
                                        muxId={image.videoSrc ?? ""}
                                        autoPlay={true}
                                        {showMuteToggle}
                                    />
                                {:else}
                                    <Image
                                        {...image as any}
                                        src={formatTallBuilderImageUrl(image.src, 1200)}
                                        sizes={MEDIA_IMAGE_SIZES}
                                    />
                                {/if}
                            </div>
                        {/each}
                    {/if}
                </div>
            {/if}
        </div>
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use "sass-mq/mq" as *;

    .media-and-text-wrapper {
        background-color: var(--bg-colour);
        color: var(--txt-colour);
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        @include mq($from: tablet) {
            display: grid;
            column-gap: var(--layout-pagegrid-gutter);
            grid-template-columns:
                [first] repeat(var(--layout-pagegrid-col-count), 1fr)
                [end];
        }

        &--has-background-colour {
            padding-block: var(--layout-pagegrid-margin);
        }

        &--layout-is-fullbleed {
            display: block;
            padding-left: 0;
            padding-right: 0;
        }
    }

    .media-and-text {
        display: flex;
        flex-direction: column;
        gap: var(--layout-pagegrid-margin);
        grid-column: 2 / -2;

        &--columns-Single {
            &.media-and-text--mediaPositionVertical-is-Top {
                @include mq($from: tablet) {
                    flex-direction: column-reverse;
                }
            }
            &.media-and-text--mediaPositionVertical-is-Bottom {
                @include mq($from: tablet) {
                    flex-direction: column;
                }
            }
        }

        &--columns-Double {
            &.media-and-text--mediaPositionHorizontal-is-Right {
                @include mq($from: tablet) {
                    flex-direction: row;
                }
            }
            &.media-and-text--mediaPositionHorizontal-is-Left {
                @include mq($from: tablet) {
                    flex-direction: row-reverse;
                }
            }

            &.media-and-text--verticalAlignment-is-Top {
                @include mq($from: tablet) {
                    align-items: start;
                }
            }
            &.media-and-text--verticalAlignment-is-Middle {
                @include mq($from: tablet) {
                    align-items: center;
                }
            }
            &.media-and-text--verticalAlignment-is-Bottom {
                @include mq($from: tablet) {
                    align-items: flex-end;
                }
            }
        }

        &--copyAlignment-is-Right {
            @include mq($from: tablet) {
                text-align: right;
            }
        }
        &--copyAlignment-is-Center {
            @include mq($from: tablet) {
                text-align: center;
            }
        }

        &__text {
            &--is-sticky {
                @include mq($from: desktop) {
                    position: sticky;
                    top: calc(10rem + var(--announcement-bar-height, 0px));
                }
            }

            .media-and-text--columns-Double.media-and-text--layoutType-Media-and-Text
                & {
                @include mq($from: tablet) {
                    flex: 0 0 calc(50% - var(--layout-pagegrid-gutter) / 2);
                }
            }
        }

        &__source {
            &--extra {
                margin: 2.4rem 0 0;

                @include mq($from: tablet) {
                    margin: 4.8rem 0 0;
                }
            }

            &--desktop {
                // Only hide on mobile if mobile variant exists
                &.media-and-text__source--has-mobile {
                    display: none;

                    @include mq($from: tablet) {
                        display: block;
                    }
                }
            }

            &--mobile {
                display: block;

                @include mq($from: tablet) {
                    display: none;
                }
            }
        }

        &__descriptions {
            .media-and-text--columns-Double.media-and-text--layoutType-Text-Only.media-and-text--hasSecondaryDescription
                & {
                @include mq($from: tablet) {
                    display: flex;
                    flex-direction: row;
                    gap: 2rem;
                }
            }
        }

        &__media {
            display: grid;
            gap: var(--layout-pagegrid-gutter);
            width: 100%;

            :global(picture),
            :global(img) {
                width: 100%;
            }

            .media-and-text--columns-Double.media-and-text--layoutType-Media-and-Text
                & {
                @include mq($from: tablet) {
                    flex: 0 0 calc(50% - var(--layout-pagegrid-gutter) / 2);
                }
            }

            .media-and-text--columns-Double.media-and-text--layoutType-Media-Only.media-and-text--hasSecondaryMedia
                & {
                @include mq($from: tablet) {
                    grid-template-columns: 1fr 1fr;
                }
            }
        }
    }

    .media-and-text :global(.section-header) {
        line-height: 1;
        margin-bottom: 1.6rem;
        margin-top: -0.7rem;

        @include mq($from: tablet) {
            margin-bottom: 3rem;
        }
    }

    .media-and-text :global(.section-text) {
        font-size: 1.4rem;
        line-height: 20px;
        max-width: 72ch;

        @include mq($from: tablet) {
            font-size: 1.8rem;
            line-height: 1.6;
        }
    }
    .media-and-text--columns-Double.media-and-text--layoutType-Text-Only.media-and-text--hasSecondaryDescription
        :global(.section-text) {
        @include mq($from: tablet) {
            flex: 0 0 calc(50% - var(--layout-pagegrid-gutter) / 2);
        }
    }

    .media-and-text--columns-Single.media-and-text--copyAlignment-is-Left
        :global(.section-text) {
        @include mq($from: tablet) {
            margin: 0 auto 0 0;
        }
    }
    .media-and-text--columns-Single.media-and-text--copyAlignment-is-Center
        :global(.section-text) {
        @include mq($from: tablet) {
            margin: 0 auto;
        }
    }
    .media-and-text--columns-Single.media-and-text--copyAlignment-is-Right
        :global(.section-text) {
        @include mq($from: tablet) {
            margin: 0 0 0 auto;
        }
    }
</style>
